import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UrlService } from './url.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedSnackbarComponent } from 'src/app/shared/components/snackbar/snackbar.component';
import { SnackbarData, SnackbarValidationMessage } from '../models/snackbar.model';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector, private zone: NgZone, private httpClient: HttpClient, private urlService: UrlService) {}

  logErrorToAdmin(message: string, details: string): void {
    const error = {
      LogLevel: 'Error',
      Category: 'Client',
      Message: message,
      Details: details
    };
    this.httpClient.post(`${environment.apiUrl}/Core/Log`, error).pipe(take(1)).subscribe();
  }

  logErrorToConsole(errorMessage: string) {
    console.log(errorMessage);
  }

  navigateToGeneralErrorPage(router: Router) {
    router.navigate(['general-error']);
  }

  navigateToConnectionErrorPage(router: Router) {
    router.navigate(['connection-error']);
  }

  navigateToLoginPage(router: Router) {
    router.navigate(['logout']);
  }

  openSnackBar(snackBar: MatSnackBar, data: SnackbarData) {
    snackBar.openFromComponent(SharedSnackbarComponent, {
      data,
      duration: 15000,
      verticalPosition: 'top',
      horizontalPosition: 'right'
    });
  }

  createValidationErrorString(validationMessages: SnackbarValidationMessage[]) {
    let validationMessageString = '';
    validationMessages.forEach(msg => {
      const messageString = `\tLevel: ${msg.Level}\n\tText: ${msg.Text}\n\tProperty: ${msg.Property}\n`;
      validationMessageString = `${validationMessageString}\n${messageString}`;
    });
    return validationMessageString;
  }

  handleError(error: any) {
    const router = this.injector.get(Router);
    const snackBar = this.injector.get(MatSnackBar);
    if (error instanceof HttpErrorResponse) {
      let errorMessage: string;
      if (error.error.Message && error.error.MessageDetail) {
        errorMessage = `Status: ${error.status}\nMessage: ${error.message}\nUrl: ${error.url}\nDetailedMessage: ${error.error.Message}\n\t${error.error.MessageDetail}`;
      } else if (error.error.Message) {
        errorMessage = `Status: ${error.status}\nMessage: ${error.message}\nUrl: ${error.url}\nDetailedMessage: ${error.error.Message}`;
      } else {
        errorMessage = `Status: ${error.status}\nMessage: ${error.message}\nUrl: ${error.url}`;
      }

      let snackbarData: SnackbarData;

      switch (error.status) {
        case 0:
          // bad connection or CORS
          if (!navigator.onLine) {
            // Completely offline or bad internet connection
            this.logErrorToConsole(errorMessage);
            this.urlService.setUrlBeforeConnectionLost(router.url);
            this.navigateToConnectionErrorPage(router);
          } else {
            // Either CORS problems or other general error, when actual status code is not available
            this.logErrorToConsole(errorMessage);
            this.navigateToGeneralErrorPage(router);
          }
          break;
        case 400:
          // Bad request
          if (error.error.Type === 'Exception') {
            errorMessage = `Status: ${error.status}\nMessage: ${error.message}\nUrl: ${error.url}\nDetailedMessage: ${error.error.Message}`;

            snackbarData = {
              type: 'error',
              title: 'UI.GeneralError.Title',
              commonMessage: 'UI.GeneralError.Message',
              adminMessage: error.error.Message,
              adminLink: true
            };
          } else if (error.error.Type === 'Validation') {
            const validationMessages: SnackbarValidationMessage[] = error.error.Arguments.find(
              item => item.Argument === 'request'
            )?.Properties;
            const validationErrorString = this.createValidationErrorString(validationMessages) || '';

            errorMessage = `Status: ${error.status}\nMessage: ${error.message}\nUrl: ${error.url}\nValidation errors:\n${validationErrorString}`;

            snackbarData = {
              type: 'validationError',
              title: 'UI.Common.InvalidDataError.Title',
              validationMessages,
              adminLink: true
            };
          } else {
            snackbarData = {
              type: 'error',
              title: 'UI.GeneralError.Title',
              commonMessage: 'UI.GeneralError.Message',
              adminMessage: `${error.error.Message}\n\t${error.error.MessageDetail}`,
              adminLink: true
            };
          }
          this.logErrorToConsole(errorMessage);
          this.openSnackBar(snackBar, snackbarData);
          break;
        case 401:
          this.navigateToLoginPage(router);
          break;
        case 403:
          // No permissions
          snackbarData = {
            type: 'error',
            commonMessage: 'UI.PermissionError.Message'
          };
          this.logErrorToConsole(errorMessage);
          this.openSnackBar(snackBar, snackbarData);
          break;
        case 404:
          // Not found errors if they are not catched in a service or a component before
          snackbarData = {
            type: 'error',
            title: 'UI.Common.NotFoundError.Title',
            message: error.error?.Message || '',
            adminLink: true
          };
          this.logErrorToConsole(errorMessage);
          this.openSnackBar(snackBar, snackbarData);
          break;
        case 500:
          // If ExceptionMessage is equal to "Unauthorized"
          if (error.error !== undefined && error.error.ExceptionMessage !== undefined && error.error.ExceptionMessage === 'Unauthorized') {
            this.navigateToLoginPage(router);
          } else {
            snackbarData = {
              type: 'error',
              title: 'UI.GeneralError.Title',
              commonMessage: 'UI.GeneralError.Message', // error.error.Message for admin and developers , for others general message translated
              adminMessage: error.error?.Message || '',
              adminLink: true
            };
            this.openSnackBar(snackBar, snackbarData);
          }
          break;
        default:
          if (error.error.Message) {
            this.logErrorToConsole(errorMessage);

            snackbarData = {
              type: 'error',
              title: 'UI.GeneralError.Title',
              commonMessage: 'UI.GeneralError.Message',
              adminMessage: error.error?.Message || '',
              adminLink: true
            };
            this.openSnackBar(snackBar, snackbarData);
          } else {
            this.navigateToGeneralErrorPage(router);
          }
          break;
      }
    } else {
      // Client-side errors
      this.logErrorToAdmin(error.name, `Message: ${error.message}\nStack: ${error.stack}`);
      this.navigateToGeneralErrorPage(router);
    }
  }
}
